import React, {useEffect, useRef, useState} from "react";
import classnames from "classnames";
import {ChevronDownIcon} from "../../theme/baseTheme";

// MUI-style pill switch (track + thumb) — theme-aware, matches the mockup.
const Switch = ({ on, onChange, disabled }) => (
    <button
        type="button"
        className={classnames('desking-switch', { on, disabled })}
        onClick={() => !disabled && onChange(!on)}
        aria-pressed={on}
        disabled={disabled}
    >
        <span className={'desking-switch__track'}/>
        <span className={'desking-switch__thumb'}/>
    </button>
)

const Divider = () => <div className={'desking-filter-divider'}/>

// Sort modes ([default_desking_settings.sort_by, label]). Best By Lender shows one
// offer per lender, as before captive offers.
const SORT_OPTIONS = [
    ['Lender', 'By Lender'],
    ['BestPayment', 'Monthly Payment Low → High'],
    ['BestLender', 'Best By Lender'],
]

// Caret used by the dropdown triggers (rotates when its menu is open).
const Caret = () => <ChevronDownIcon className={'desking-dd__caret'}/>

// Dropdown: a pill trigger + a shelf that closes on outside-click.
const Dropdown = ({ label, count, open, setOpen, shelfStyle, children }) => {
    const ref = useRef(null)
    useEffect(() => {
        if (!open) return
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
        document.addEventListener('mousedown', onDoc)
        return () => document.removeEventListener('mousedown', onDoc)
    }, [open, setOpen])

    return (
        <div className={'desking-filter-group desking-dd'} ref={ref}>
            <button
                type="button"
                className={classnames('desking-dd__trigger', { open })}
                onClick={() => setOpen(!open)}
            >
                <span>{label}</span>
                { count ? <span className={'desking-dd__count'}>{count}</span> : null }
                <Caret/>
            </button>
            <div className={classnames('desking-dd__shelf', { open })} style={shelfStyle}>
                {children}
            </div>
        </div>
    )
}

export const DeskingFilterBar = (props) => {
    const {
        hasTiers,
        terms,
        captiveLenderCount,

        creditScore,
        setCreditScore,

        bestByLender,
        setBestByLender,

        captiveOnly,
        setCaptiveOnly,

        sortBy,
        setSortBy,

        selectedTerms,
        setSelectedTerms,

        exceedMaxAdvance,
        setExceedMaxAdvance,

        canSaveDefaults,
        onSaveDefaults,
    } = props

    // Only one dropdown menu is open at a time.
    const [openMenu, setOpenMenu] = useState(null)   // 'tier' | 'sort' | 'terms' | null

    const toggleTerm = (term) => {
        setSelectedTerms((prev) =>
            prev.includes(term) ? prev.filter((t) => t !== term) : [...prev, term]
        )
    }

    // "All" pill — selects every available term, or clears when all are already on.
    const allSelected = terms.length > 0 && terms.every((t) => selectedTerms.includes(t))
    const someSelected = selectedTerms.length > 0 && !allSelected
    const toggleAll = () => setSelectedTerms(allSelected ? [] : [...terms])

    const tierLabel = bestByLender ? 'Best Payment' : 'All Tiers'
    const sortLabel = SORT_OPTIONS.find(([mode]) => mode === sortBy)?.[1] ?? 'By Lender'

    return (
        <div className={'desking-filters'}>
            <div className={'desking-filter-bar'}>

                {/* Credit Score */}
                <div className={'desking-filter-group'}>
                    <span className={'desking-filter-label'}>Credit Score</span>
                    <input
                        className={'desking-filter-input'}
                        type={'number'}
                        min={300}
                        max={850}
                        placeholder={'e.g. 720'}
                        value={creditScore}
                        onChange={(e) => setCreditScore(e.target.value)}
                    />
                </div>
                <Divider/>

                {/* Tier mode (only when the data has tiers) */}
                {
                    hasTiers &&
                    <>
                        <Dropdown
                            label={tierLabel}
                            open={openMenu === 'tier'}
                            setOpen={(v) => setOpenMenu(v ? 'tier' : null)}
                            shelfStyle={{ minWidth: '170px' }}
                        >
                            <div
                                className={classnames('desking-sort-option', { selected: !bestByLender })}
                                onClick={() => { setBestByLender(false); setOpenMenu(null) }}
                            >
                                All Tiers
                            </div>
                            <div
                                className={classnames('desking-sort-option', { selected: bestByLender })}
                                onClick={() => { setBestByLender(true); setOpenMenu(null) }}
                            >
                                Best Payment
                            </div>
                        </Dropdown>
                        <Divider/>
                    </>
                }

                {/* Sort */}
                <Dropdown
                    label={sortLabel}
                    open={openMenu === 'sort'}
                    setOpen={(v) => setOpenMenu(v ? 'sort' : null)}
                    shelfStyle={{ minWidth: '230px' }}
                >
                    {SORT_OPTIONS.map(([mode, label]) => (
                        <div
                            key={mode}
                            className={classnames('desking-sort-option', { selected: sortBy === mode })}
                            onClick={() => { setSortBy(mode); setOpenMenu(null) }}
                        >
                            {label}
                        </div>
                    ))}
                </Dropdown>
                <Divider/>

                {/* Terms — pill grid */}
                <Dropdown
                    label={'Terms'}
                    count={someSelected ? selectedTerms.length : null}
                    open={openMenu === 'terms'}
                    setOpen={(v) => setOpenMenu(v ? 'terms' : null)}
                >
                    <div className={'desking-terms-grid'}>
                        <span
                            className={classnames('desking-term-pill desking-term-pill--all', { selected: allSelected })}
                            onClick={toggleAll}
                        >
                            All
                        </span>
                        {
                            terms.map((term) =>
                                <span
                                    key={term}
                                    className={classnames('desking-term-pill', { selected: selectedTerms.includes(term) })}
                                    onClick={() => toggleTerm(term)}
                                >
                                    {term}
                                </span>
                            )
                        }
                    </div>
                </Dropdown>
                <Divider/>

                {/* Captive Only (only when at least one captive lender exists) */}
                {
                    captiveLenderCount > 0 &&
                    <>
                        <div className={'desking-filter-group'}>
                            <span className={'desking-filter-label'}>Captive Only</span>
                            <Switch on={captiveOnly} onChange={setCaptiveOnly}/>
                        </div>
                        <Divider/>
                    </>
                }

                {/* Allow to Exceed Max Advance

                <div className={'desking-filter-group'}>
                    <span className={'desking-filter-label'}>Allow to Exceed Max Advance</span>
                    <Switch on={exceedMaxAdvance} onChange={setExceedMaxAdvance}/>
                </div>

                */}

                {/* Save As Defaults — shown only when the filters differ from what's
                    saved; its space is kept so the bar doesn't reflow when it appears */}
                <button
                    type="button"
                    className={classnames('desking-save-defaults', { 'is-hidden': !canSaveDefaults })}
                    disabled={!canSaveDefaults}
                    aria-hidden={!canSaveDefaults}
                    onClick={onSaveDefaults}
                >
                    Save As Defaults
                </button>

            </div>
        </div>
    )
}
